import React from 'react'
import { Card, Spinner, Table } from 'react-bootstrap'
import { Calendar, TrendingUp, Clock, AlertTriangle, Shield } from 'lucide-react'
import { useRenewalSummary, useRenewalsRegions } from '@data/renewals/renewals'
import { IRenewalSummaryCard, IRenewalRegionRow } from '@data/renewals/model/renewals.model'
import { RenewalsListFilters } from './RenewalsList'
import './Renewals.scss'

const ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  'Devices Due in 7 Days': Calendar,
  'Devices Due in 30 Days': TrendingUp,
  'Devices Due in 60 Days': Clock,
  'Overdue Devices': AlertTriangle,
  'Restricted Devices': Shield,
}

// Map a KPI card to the filter it should apply when clicked.
function filterForCard(label: string): Partial<RenewalsListFilters> | null {
  switch (label) {
    case 'Devices Due in 7 Days':
      return { health: 'upcoming', dueWindow: 7 }
    case 'Devices Due in 30 Days':
      return { health: 'upcoming', dueWindow: 30 }
    case 'Devices Due in 60 Days':
      return { health: 'upcoming', dueWindow: 60 }
    case 'Overdue Devices':
      return { health: 'overdue', dueWindow: 'overdue' }
    case 'Restricted Devices':
      return { health: 'restricted' }
    default:
      return null
  }
}

interface KpiCardProps {
  card: IRenewalSummaryCard
  onClick?: () => void
}

function KpiCard({ card, onClick }: KpiCardProps) {
  const Icon = ICONS[card.label] ?? Calendar
  return (
    <div
      className={`sf-kpi-card sf-kpi-card--${card.color}`}
      role={onClick ? 'button' : undefined}
      style={onClick ? { cursor: 'pointer' } : undefined}
      onClick={onClick}
    >
      <div className="sf-kpi-card__header">
        <span>{card.label}</span>
        <Icon size={14} />
      </div>
      <div>
        <div className="sf-kpi-card__count">{card.count}</div>
        <div className="sf-kpi-card__sublabel">Devices</div>
      </div>
    </div>
  )
}

interface Props {
  onJumpToList: (partialFilters: Partial<RenewalsListFilters>) => void
}

const RenewalsDashboard: React.FC<Props> = ({ onJumpToList }) => {
  const { data: summary, isLoading: loadingSummary } = useRenewalSummary()
  const { data: regions, isLoading: loadingRegions } = useRenewalsRegions()

  return (
    <div className="p-3">
      <h4 className="mb-3">Renewals — Dashboard</h4>

      {loadingSummary ? (
        <Spinner animation="border" className="mb-4" />
      ) : (
        <div className="row g-3 mb-4">
          {(summary ?? []).map((card, i) => {
            const filter = filterForCard(card.label)
            return (
              <div key={i} className="col-12 col-md-6 col-xl-2">
                <KpiCard
                  card={card}
                  onClick={filter ? () => onJumpToList(filter) : undefined}
                />
              </div>
            )
          })}
        </div>
      )}

      <Card>
        <Card.Body>
          <div className="sf-card-title">REGIONAL OVERVIEW</div>
          {loadingRegions ? (
            <Spinner animation="border" />
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr className="text-muted small">
                  <th>REGION NAME</th>
                  <th>CUSTOMERS</th>
                  <th>LOCATIONS</th>
                  <th>DEVICES</th>
                  <th className="text-danger">PAST DUE</th>
                  <th>RESTRICTED</th>
                </tr>
              </thead>
              <tbody>
                {(regions ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      No regions found
                    </td>
                  </tr>
                ) : (
                  (regions ?? []).map((r: IRenewalRegionRow) => (
                    <tr key={r.region}>
                      <td>
                        <button
                          type="button"
                          className="sf-region-link btn btn-link p-0"
                          onClick={() => onJumpToList({ region: r.region })}
                        >
                          {r.display_name}
                        </button>
                      </td>
                      <td>{r.customers}</td>
                      <td>{r.locations}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => onJumpToList({ region: r.region })}
                        >
                          {r.devices}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`btn btn-link p-0 ${r.past_due > 0 ? 'sf-past-due' : 'text-muted'}`}
                          onClick={() => onJumpToList({ region: r.region, health: 'overdue' })}
                        >
                          {r.past_due}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-dark"
                          onClick={() => onJumpToList({ region: r.region, health: 'restricted' })}
                        >
                          {r.restricted}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default RenewalsDashboard
